import { useState } from 'react';
import StyledInput from '../styled/StyledInput';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { useCreateUser } from '@/api/user.api';
import { useToast } from '@/hooks/useToast';
import { UserType } from '@/types/auth/UserType';
import { PATHS } from '@/constants/Paths';
import StyledButton from '../styled/StyledButton';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import StyledDropZone from '../styled/StyledDropZone';
import StyledSelect from '../styled/StyledSelect';
import {
  COUNTRIES,
  COUNTRIES_DATA,
  CURRENCIES,
} from '../../../../shared/CountriesData';
import SendIcon from '@/assets/icons/send-01.svg?react';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import {
  ReceiverRegisterFormData,
  ReceiverRegisterSchema,
} from '@/types/auth/ReceiverRegisterFormData';
import ReceiverContacts from '../profile/ReceiverContacts';
import ReceiverReservationEmails from '../profile/ReceiverReservationEmails';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import { useTranslation } from '@/i18n';

const ReceiverRegisterForm = () => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ReceiverRegisterFormData>({
    resolver: zodResolver(ReceiverRegisterSchema),
    mode: 'onChange',
    defaultValues: {
      firstname: undefined,
      lastname: undefined,
      email: undefined,
      password: undefined,
      company: {
        contacts: [
          {
            lastName: undefined,
            firstName: undefined,
            email: undefined,
            phoneNumber: undefined,
          },
        ],
        reservationEmails: [{ email: undefined }],
      },
    },
  });

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const firstName = useWatch({ control, name: 'firstname' });
  const lastName = useWatch({ control, name: 'lastname' });
  const email = useWatch({ control, name: 'email' });
  const password = useWatch({ control, name: 'password' });

  const [isFullFormModalOpen, setIsFullFormModalOpen] = useState(false);

  const { mutateAsync: createUser } = useCreateUser();
  const { toastAsync } = useToast();

  const changeCountryWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'company.country',
    secondaryName: 'company.currency',
    retrieveSecondary: (primaryValue) => {
      const countryData = COUNTRIES_DATA.find(
        (data) => data.Pays === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.label === countryData?.Devise,
      );
      return currency;
    },
  });

  const submitForm: SubmitHandler<ReceiverRegisterFormData> = async (data) => {
    setErrorMessage(undefined);
    const formData = new FormData();
    const addIf = (key: string, value: string | undefined) => {
      if (value !== undefined) formData.append(key, value);
    };
    formData.append('userType', UserType.Receiver);
    formData.append('companyName', data.company.name);
    if (data.company.logo) {
      formData.append('file', data.company.logo);
    }
    addIf('address', data.company.address);
    addIf('city', data.company.city);
    if (data.company.postalCode)
      formData.append('postalCode', data.company.postalCode.toString());
    addIf('country', data.company.country?.value);
    addIf('currency', data.company.currency?.value);
    addIf('website', data.company.website);
    formData.append('firstname', data.firstname);
    formData.append('lastname', data.lastname);
    formData.append('email', data.email);
    formData.append('password', data.password);
    if (data.company?.reservationEmails)
      data.company.reservationEmails.forEach(({ email }) => {
        if (email) formData.append('reservationEmails[]', email);
      });
    if (data.company?.contacts) {
      formData.append(
        'contacts',
        JSON.stringify(
          data.company.contacts.map((contact) => ({
            ...contact,
            name: contact.lastName,
            lastName: undefined,
          })),
        ),
      );
    }

    const response = await toastAsync(createUser(formData), {
      success: t('auth.receiverRegister.accountCreatedSuccess'),
    });

    if (response.status >= 400) {
      setErrorMessage(t('auth.receiverRegister.accountCreationError'));
      return;
    }

    navigate(PATHS.auth.login);
  };

  return (
    <>
      <form
        className="flex w-full flex-col gap-6"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex flex-col w-full gap-5">
          <StyledInput
            control={control}
            title={t('auth.receiverRegister.lastName')}
            name="lastname"
            type="text"
            placeholder={t('auth.receiverRegister.enterLastName')}
          />
          <StyledInput
            control={control}
            title={t('auth.receiverRegister.firstName')}
            name="firstname"
            type="text"
            placeholder={t('auth.receiverRegister.enterFirstName')}
          />
          <StyledInput
            control={control}
            title={t('auth.receiverRegister.email')}
            name="email"
            type="email"
            placeholder={t('auth.receiverRegister.enterEmail')}
          />
          <StyledInput
            control={control}
            title={t('auth.receiverRegister.password')}
            name="password"
            type="password"
          />
        </div>

        {errorMessage && (
          <p className="text-xs text-error-500">{errorMessage}</p>
        )}

        <div className="flex w-full flex-col gap-2">
          <StyledButton
            title={t('auth.receiverRegister.openMyAccount')}
            className="px-4 py-2.5 gap-1.5 rounded-lg"
            disabled={!firstName || !lastName || !email || !password}
            onClick={() => {
              setIsFullFormModalOpen(true);
            }}
          />
        </div>
      </form>
      {isFullFormModalOpen && (
        <dialog
          open
          className="fixed top-0 left-auto w-2/3 bg-brand-25 max-h-full overflow-auto"
        >
          <StyledContainer>
            <StyledFormHeaderWithBreadcrumb
              title={t('auth.receiverRegister.welcome', {
                firstName: firstName ?? '',
              })}
              breadcrumb={
                <StyledButton
                  variant="link"
                  title={t('auth.receiverRegister.back')}
                  className="text-sm text-[#3D3D3D] pl-0"
                  onClick={() => setIsFullFormModalOpen(false)}
                  icon={<ArrowLeftIcon />}
                />
              }
              subTitle={t('auth.receiverRegister.completeRequiredFields')}
            ></StyledFormHeaderWithBreadcrumb>
            <HorizontalLine />
            <StyledFormSection
              sectionTitle={t('auth.receiverRegister.generalInfo')}
              shouldTakeHalfWidth
            >
              <StyledDropZone
                control={control}
                name="company.logo"
                title={t('auth.receiverRegister.logo')}
                placeholder={t('auth.receiverRegister.logoPlaceholder')}
              />
              <StyledInput
                control={control}
                name="company.name"
                title={t('auth.receiverRegister.companyName')}
              />
              <div className="flex flex-col md:flex-row gap-4">
                <StyledInput
                  control={control}
                  name="lastname"
                  title={t('auth.receiverRegister.lastName') + ' *'}
                />
                <StyledInput
                  control={control}
                  name="firstname"
                  title={t('auth.receiverRegister.firstName') + ' *'}
                />
              </div>
            </StyledFormSection>
            <HorizontalLine />
            <StyledFormSection
              sectionTitle={t('auth.receiverRegister.loginCredentials')}
              shouldTakeHalfWidth
            >
              <div className="flex flex-col md:flex-row gap-4">
                <StyledInput
                  control={control}
                  name="email"
                  title={t('auth.receiverRegister.email') + ' *'}
                  type="email"
                />
                <StyledInput
                  control={control}
                  name="password"
                  type="password"
                  placeholder="**********"
                  title={t('auth.receiverRegister.password') + ' *'}
                />
              </div>
            </StyledFormSection>
            <HorizontalLine />
            <StyledFormSection
              sectionTitle={t('auth.receiverRegister.administrativeInfo')}
              shouldTakeHalfWidth
            >
              <StyledInput
                control={control}
                name="company.address"
                title={t('auth.receiverRegister.address')}
                placeholder={t('auth.receiverRegister.addAddress')}
              />
              <div className="flex flex-col md:flex-row gap-4">
                <StyledInput
                  control={control}
                  name="company.city"
                  title={t('auth.receiverRegister.city')}
                  placeholder={t('auth.receiverRegister.addCity')}
                />
                <StyledInput
                  control={control}
                  name="company.postalCode"
                  type="number"
                  title={t('auth.receiverRegister.postalCode')}
                  hideNumberFieldArrows
                  placeholder={t('auth.receiverRegister.addPostalCode')}
                />
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <StyledSelect
                  options={COUNTRIES}
                  control={control}
                  name="company.country"
                  title={t('auth.receiverRegister.country')}
                  placeholder={t('auth.receiverRegister.selectCountry')}
                  onChange={changeCountryWithCurrency}
                />
                <StyledSelect
                  options={CURRENCIES}
                  control={control}
                  name="company.currency"
                  title={t('auth.receiverRegister.currency')}
                  placeholder={t('auth.receiverRegister.selectCurrency')}
                />
              </div>
              <StyledInput
                control={control}
                name="company.website"
                title={t('auth.receiverRegister.website')}
                placeholder={t('auth.receiverRegister.websitePlaceholder')}
              />
              <ReceiverContacts control={control} />
              <ReceiverReservationEmails control={control} />
            </StyledFormSection>
            <div className="flex flex-row-reverse p-6">
              <StyledButton
                title={t('auth.receiverRegister.sendRequest')}
                onClick={handleSubmit(submitForm)}
                disabled={!isValid}
                icon={<SendIcon className="size-5" />}
              />
            </div>
          </StyledContainer>
        </dialog>
      )}
    </>
  );
};

export default ReceiverRegisterForm;
